import DraftJS from 'draft-js'
import { AdvancedSettingsPreset } from './AdvancedSettingsPreset'
import { TrainingJobTier } from './MLTrainingJob'
import { VoiceModelSubmissionLanguage } from './VoiceModelSubmission'
import { Attachment, BaseModel } from './common'

export enum VoiceType {
  SINGING = 'singing',
  SPEAKING = 'speaking',
  RAPPING = 'rapping',
  INSTRUMENT = 'instrument'
}

export enum VoiceModelLicense {
  ROYALTIES_APPLIED = 'Royalties applied',
  ROYALTY_FREE = 'Royalty-free',
  INSTRUMENT = 'Instrument'
}

export enum VoiceModelPublishStatus {
  PRIVATE = 'private',
  PUBLIC = 'public'
}

export enum VoiceModelOrigin {
  PTH_UPLOADED = 'pth-uploaded',
  TRAINED = 'trained',
  MERGED = 'merged',
  REFERENCE_FILE = 'reference-file'
}

export enum VoiceModelSharedType {
  INCLUDE = 'include',
  ONLY = 'only'
}

export enum VoiceModelSubmissionType {
  INCLUDE = 'include',
  ONLY = 'only'
}

export enum VoiceModelLikeType {
  INCLUDE = 'include',
  ONLY = 'only'
}

export interface EvalScores {
  v1: EvalScoresV1 | null
}

export interface EvalScoresV1 {
  input_file_scores: EvalScoresV1File[]
  // Overall averages
  averages: EvalScoresV1File
  // Broken down by category
  categories: EvalScoresV1Category[]
  // S3 directory containing the csv file and output files.
  results_s3_url: string
  // S3 key pointing to the model file that eval was run on. This is useful because
  // if we manually update the model file on the voice model, and the pth file doesn't match
  // this one, then we know we need to re-run eval.
  model_s3_url: string
  // S3 url pointing to the speaker reference file used in the eval.
  speaker_reference_url: string | null
}

export interface EvalScoresV1Category {
  name: string
  scores: EvalScoresV1File[]
  averageScores: EvalScoresV1File | undefined
}

export interface EvalScoresV1File {
  name: string
  // This is output file, can actually be lossy or lossless
  s3_lossy_file_url: string | null
  s3_input_file_url: string | null
  overall_mos: number | null
  naturalness: number | null
  UTMOS: number | null
  hubert: number | null
  speaker_similarity: number | null
}

export interface VoiceModelMetadata {
  language?: VoiceModelSubmissionLanguage
  isVoiceDesigner?: boolean
  isVoiceVariant?: boolean
}

export interface VoiceModel extends BaseModel {
  title: string
  slug: string
  description: DraftJS.RawDraftContentState
  publishedAt: string
  publishedToLibraryAt: string | null
  license: VoiceModelLicense
  tags: string[] | null
  isUserCreated: boolean
  publishStatus: VoiceModelPublishStatus
  origin: VoiceModelOrigin
  modelUrl: string | null
  referenceFileUrls: string[] | null
  indexFileUrl: string | null
  image: Attachment
  demo: Attachment | null
  split: number | null
  twitterLink: string | null
  instagramLink: string | null
  tiktokLink: string | null
  spotifyLink: string | null
  youtubeLink: string | null
  youtubeDemoLink: string | null
  likeCount: number
  likedByUser: boolean
  aiUserId: number
  mlTrainingJobId: number
  tier: TrainingJobTier
  usageCount: number
  minPitch: number | null
  maxPitch: number | null
  defaultPreset: AdvancedSettingsPreset | null
  isIncompatible: boolean
  isMerged: boolean
  isTraining: boolean
  isZeroShot: boolean
  isQuickTrain: boolean
  isFreeTierViableModel: boolean
  isUsable: boolean
  voiceType: VoiceType | null
  isSubmission: boolean
  evalScores: EvalScores | null
  netEvalScoreV1: number | null
  evalScoresTable: string | null
  metadata: VoiceModelMetadata
}
